import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import * as UserActions from '../actions/user.actions';
import * as UserSelctors from '../selectors/user.selectors';
import * as UserState from '../reducers/user.reducer';
import {
  AppAccessLevel,
  GroupUsers,
  UpdateUserEmail,
  User,
  UserGroup,
  UserStatus,
} from '@sleipai/sleip-models';
import { createChunksOfTen, QueryConfig } from '../../helpers';
import { ClientWebAppProjectType } from '../../env-configuration';

@Injectable()
export class UserStoreFacade {
  constructor(private readonly store: Store<UserState.State>) {}

  currentUserHorsesLoadedInfo$: Observable<boolean | null> = this.store.pipe(
    select(UserSelctors.selectUserHorseLoadStatusById)
  );

  isUserLoaded$ = (userId: string) =>
    this.store.pipe(
      select(UserSelctors.selectSingleUser(userId)),
      map((horseShareItem: User | null) => horseShareItem !== null)
    );

  public getAllUsers$ = this.store.pipe(select(UserSelctors.selectAllUsers));

  fetchSingleUser$ = (userId: string) =>
    this.store.dispatch(UserActions.loadSingleUser({ uid: userId }));

  getSingleUser$ = (userId: string) =>
    this.store.pipe(select(UserSelctors.selectSingleUser(userId)));

  getUsersByIds$ = (userIds: string[]) =>
    this.store.pipe(select(UserSelctors.selectUsersByIds(userIds)));

  fetchUsersByIds$ = (userIds: string[]) => {
    const chunks = createChunksOfTen(userIds);
    chunks.map((uids: any) => {
      this.store.dispatch(UserActions.loadUsersByIds({ uids }));
    });
  };

  getTotalUsersCount$: Observable<number> = this.store.pipe(
    select(UserSelctors.selectTotalUserCount)
  );

  getNumberOfPages$: Observable<number> = this.store.pipe(
    select(UserSelctors.selectNumberOfPages)
  );

  fetchUsersListNextPage$ = (
    queryConfig: QueryConfig,
    updateTotalCount: boolean = false
  ) => {
    this.store.dispatch(
      UserActions.loadUsersFromAlgoliaSidb({ queryConfig, updateTotalCount })
    );
  };

  getUsersListPage$ = (
    queryConfig: QueryConfig,
    clientWebAppProject?: ClientWebAppProjectType
  ) => {
    return this.store.pipe(
      select(UserSelctors.selectUsersListPage(queryConfig, clientWebAppProject))
    );
  };

  getIsLoading$: Observable<boolean> = this.store.pipe(
    select(UserSelctors.selectIsLoading)
  );

  createUser$ = (userToCreate: any) =>
    this.store.dispatch(UserActions.createUser(userToCreate));

  getUserActionSuccessType$ = this.store.pipe(
    select(UserSelctors.selectUserActionSuccessType)
  );

  getUserSaved$: Observable<boolean> = this.store.pipe(
    select(UserSelctors.selectUserSaved)
  );

  getUserError$: Observable<string | null> = this.store.pipe(
    select(UserSelctors.selectUserError)
  );

  updateUserRemoteShareRightsSetting$ = (
    userId: string,
    remoteShareRights: boolean
  ) => {
    this.store.dispatch(
      UserActions.updateUserRemoteShareRightsSetting({
        userId,
        remoteShareRights,
      })
    );
  };

  updateUserDesktopWebAppAccessSetting$ = (
    userEmail: string,
    userId: string,
    desktopWebAppAccess: boolean
  ) => {
    this.store.dispatch(
      UserActions.updateUserDesktopWebAppAccessSetting({
        userEmail,
        userId,
        desktopWebAppAccess,
      })
    );
  };

  updateUserIsAdminSetting$ = (userId: string, isAdmin: boolean) => {
    this.store.dispatch(
      UserActions.updateUserIsAdminSetting({
        userId,
        isAdmin,
      })
    );
  };

  updateUserBetaTesterSetting$ = (userId: string, betaTester: boolean) => {
    this.store.dispatch(
      UserActions.updateUserBetaTesterSetting({
        userId,
        betaTester,
      })
    );
  };

  updateUserGroupSharingSetting$ = (groupId: string, groupUser: GroupUsers) => {
    this.store.dispatch(
      UserActions.updateUserGroupSharingSetting({
        groupId,
        groupUser,
      })
    );
  };

  updateUserName$ = (user: User, name: string) => {
    this.store.dispatch(
      UserActions.updateUserName({
        user,
        name,
      })
    );
  };

  updateUserCountry$ = (user: User, country: string) => {
    this.store.dispatch(
      UserActions.updateUserCountry({
        user,
        country,
      })
    );
  };

  updateUserUserStatus$ = (user: User, userStatus: UserStatus) => {
    this.store.dispatch(
      UserActions.updateUserUserStatus({
        user,
        userStatus,
      })
    );
  };

  updateUserTypeAppAccess$ = (user: User, appAccess: AppAccessLevel) => {
    this.store.dispatch(
      UserActions.updateUserTypeAppAccess({
        user,
        appAccess,
      })
    );
  };

  updateUserEmail$ = (user: User, updatedEmail: string) => {
    if (!user.id || !user.uid) return;
    const userEmailUpdateInfo: UpdateUserEmail = {
      userId: user.id || user.uid,
      newEmail: updatedEmail,
    };
    this.store.dispatch(
      UserActions.updateUserEmail({
        user,
        userEmailUpdateInfo,
      })
    );
  };

  sendResetPasswordEmailForUser$ = (userEmail: string) => {
    this.store.dispatch(
      UserActions.sendResetPasswordEmailForUser({
        userEmail,
      })
    );
  };

  updateGroupInUser$ = (user: User, group: UserGroup) => {
    this.store.dispatch(
      UserActions.updateGroupInUser({
        user,
        group,
      })
    );
  };

  removeUser$ = (userToDelete: User) =>
    this.store.dispatch(UserActions.removeUser({ userToDelete }));

  fetchAdminUsers$ = () => this.store.dispatch(UserActions.loadAdminUsers());

  getAdminUsers$ = this.store.pipe(select(UserSelctors.selectAdminUsers));

  fetchUsersForInfiniteScrollList$ = (
    queryConfig: QueryConfig,
    reset: boolean
  ) => {
    this.store.dispatch(
      UserActions.loadUsersForInfiniteScrollList({ queryConfig, reset })
    );
  };

  getHasMoreUsers$: Observable<boolean> = this.store.pipe(
    select(UserSelctors.selectUsersHasMore)
  );
}
